import React, { useEffect, useState, useRef } from "react";
import { CiCalendar } from "react-icons/ci";
import { FiUser, FiMenu, FiX, FiChevronDown } from "react-icons/fi";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { scrollToSection } from "../functions/scrollingFunctions";
import BookingButton from "./utilities/BookingButton";
import useAuthStore from "../store/authStore";
import logo from "../assets/logo/logo-100.png";

const NavbarSection = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location.pathname;
  const dropdownRef = useRef(null);
  const mobileDropdownRef = useRef(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileDropdownOpen, setIsMobileDropdownOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const { user, getUser, logout } = useAuthStore();

  useEffect(() => {
    getUser();
  }, [getUser]);

  // Fermer le dropdown si on clique à l'extérieur
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
      if (
        mobileDropdownRef.current &&
        !mobileDropdownRef.current.contains(event.target)
      ) {
        setIsMobileDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const toggleMenu = () => setMenuOpen(!menuOpen);

  const toggleDropdown = () => setIsDropdownOpen(!isDropdownOpen);

  const toggleMobileDropdown = () =>
    setIsMobileDropdownOpen(!isMobileDropdownOpen);

  const handleLogout = () => {
    logout();
    setIsDropdownOpen(false);
    setIsMobileDropdownOpen(false);
    setMenuOpen(false);
    navigate("/index");
  };

  const isActive = (target) => {
    return currentPath === target;
  };

  const handleDropdownItemClick = () => {
    setIsDropdownOpen(false);
    setMenuOpen(false);
  };

  const handleMobileDropdownItemClick = () => {
    setIsMobileDropdownOpen(false);
    setMenuOpen(false);
  };

  // Contenu du dropdown pour utilisateur connecté (Desktop)
  const UserDropdownContent = ({ isMobile = false }) => (
    <div
      className={`${isMobile ? "w-full mt-2" : "absolute right-0 mt-2 p-2 w-48 bg-white text-black shadow-lg rounded-md border border-gray-100"}`}
    >
      <Link
        to="/profil"
        onClick={
          isMobile ? handleMobileDropdownItemClick : handleDropdownItemClick
        }
        className={`block px-4 py-2 hover:bg-gray-50 rounded font-nunito text-[14px] text-[#333] transition-colors ${
          isActive("/profil") ? "bg-gray-50 font-semibold" : ""
        }`}
      >
        Mon profil
      </Link>

      {user?.role?.name === "client" && (
        <>
          <Link
            to="/rendez-vous"
            onClick={
              isMobile ? handleMobileDropdownItemClick : handleDropdownItemClick
            }
            className={`block px-4 py-2 hover:bg-gray-50 rounded font-nunito text-[14px] text-[#333] transition-colors ${
              isActive("/rendez-vous") ? "bg-gray-50 font-semibold" : ""
            }`}
          >
            Mes rendez-vous
          </Link>
          <Link
            to="/mes-achats"
            onClick={
              isMobile ? handleMobileDropdownItemClick : handleDropdownItemClick
            }
            className={`block px-4 py-2 hover:bg-gray-50 rounded font-nunito text-[14px] text-[#333] transition-colors ${
              isActive("/mes-achats") ? "bg-gray-50 font-semibold" : ""
            }`}
          >
            Mes Paiements
          </Link>
          <Link
            to="/mes-abonnements"
            onClick={
              isMobile ? handleMobileDropdownItemClick : handleDropdownItemClick
            }
            className={`block px-4 py-2 hover:bg-gray-50 rounded font-nunito text-[14px] text-[#333] transition-colors ${
              isActive("/mes-abonnements") ? "bg-gray-50 font-semibold" : ""
            }`}
          >
            Mes Abonnements
          </Link>
        </>
      )}

      {user?.role?.name !== "client" && (
        <Link
          to="/admin"
          onClick={
            isMobile ? handleMobileDropdownItemClick : handleDropdownItemClick
          }
          className={`block px-4 py-2 hover:bg-gray-50 rounded font-nunito text-[14px] text-[#333] transition-colors ${
            isActive("/admin") ? "bg-gray-50 font-semibold" : ""
          }`}
        >
          Espace admin
        </Link>
      )}

      <div className="border-t border-gray-100 mt-2 pt-2">
        <button
          onClick={handleLogout}
          className="block w-full text-left px-4 py-2 hover:bg-red-50 rounded font-nunito font-semibold text-[14px] text-[#FF4081] transition-colors"
        >
          Déconnexion
        </button>
      </div>
    </div>
  );

  return (
    <>
      {/* Navbar */}
      <nav
        className={`fixed top-0 w-full self-center
                 z-50 backdrop-blur-sm bg-white text-black transition-all duration-300 ease-in-out
                 shadow-md border-b border-gray-300
        `}
      >
        <div className="flex justify-between items-center px-6 py-4">
          <div className="text-lg font-bold">
            <img src={logo} alt="Logo" />
          </div>

          {/* Desktop Links */}
          <div className="hidden lg:flex gap-6 items-center text-sm">
            <button
              onClick={() => scrollToSection(navigate, "/", "accueil")}
              className={`font-poppins font-medium text-[15px] text-[#444] hover:text-[#FF4081] transition-colors ${
                isActive("/") ? "text-[#FF4081] font-semibold underline" : ""
              }`}
            >
              Accueil
            </button>
            <button
              onClick={() => scrollToSection(navigate, "/services", "")}
              className={`font-poppins font-medium text-[15px] text-[#444] hover:text-[#FF4081] transition-colors ${
                isActive("/services")
                  ? "text-[#FF4081] font-semibold underline"
                  : ""
              }`}
            >
              Services
            </button>
            <button
              onClick={() => scrollToSection(navigate, "/plan-abonnements", "")}
              className={`font-poppins font-medium text-[15px] text-[#444] hover:text-[#FF4081] transition-colors ${
                isActive("/plan-abonnements")
                  ? "text-[#FF4081] font-semibold underline"
                  : ""
              }`}
            >
              Plan d'abonnements
            </button>

            <button
              onClick={() => scrollToSection(navigate, "/", "apropos")}
              className={`font-poppins font-medium text-[15px] text-[#444] hover:text-[#FF4081] transition-colors ${
                isActive("/about")
                  ? "text-[#FF4081] font-semibold underline"
                  : ""
              }`}
            >
              A Propos
            </button>
            <button
              onClick={() => scrollToSection(navigate, "/", "contact")}
              className={`font-poppins font-medium text-[15px] text-[#444] hover:text-[#FF4081] transition-colors ${
                isActive("/contact")
                  ? "text-[#FF4081] font-semibold underline"
                  : ""
              }`}
            >
              Contact
            </button>

            <BookingButton label="Réserver" />

            {user ? (
              <div
                className="relative"
                ref={dropdownRef}
                onMouseEnter={() => setIsDropdownOpen(true)}
              >
                <button
                  onClick={toggleDropdown}
                  className="flex items-center gap-2 font-poppins font-medium text-[15px] text-[#444] hover:text-[#FF4081] transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                      <FiUser size={16} />
                    </div>
                    <span>{`${user.prenom} ${user.nom}` || "Profil"}</span>
                  </div>
                  <FiChevronDown
                    size={16}
                    className={`transition-transform duration-200 ${isDropdownOpen ? "rotate-180" : ""}`}
                  />
                </button>

                {/* Dropdown Desktop */}
                {isDropdownOpen && (
                  <div
                    className="absolute right-0 mt-2 z-50"
                    onMouseLeave={() => setIsDropdownOpen(false)}
                  >
                    <UserDropdownContent />
                  </div>
                )}
              </div>
            ) : (
              <Link
                to="/login"
                className="flex items-center gap-2 font-poppins font-medium text-[15px] text-[#444] hover:text-[#FF4081] transition-colors"
              >
                <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                  <FiUser size={16} />
                </div>
                <span>Connexion</span>
              </Link>
            )}
          </div>

          {/* Mobile Menu Toggle */}
          <div className="lg:hidden">
            <button onClick={toggleMenu}>
              {menuOpen ? <FiX size={24} /> : <FiMenu size={24} />}
            </button>
          </div>
        </div>
      </nav>

      {/* Overlay */}
      {menuOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-40 z-20 transition-opacity duration-300"
          onClick={toggleMenu}
        />
      )}

      {/* Offcanvas Menu (Left side) */}
      <div
        className={`fixed top-0 left-0 h-full w-64 bg-white shadow-lg z-30 transform transition-transform duration-300 ease-in-out ${
          menuOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex justify-between items-center px-6 py-4 border-b">
          <div className="font-poppins font-semibold text-[18px] text-[#222]">
            Menu
          </div>
          <button onClick={toggleMenu}>
            <FiX size={24} />
          </button>
        </div>
        <div className="flex flex-col gap-4 px-6 py-6 text-sm">
          <button
            onClick={() => {
              scrollToSection(navigate, "/", "accueil");
              toggleMenu();
            }}
            className={`w-full text-left font-poppins font-medium text-[15px] text-[#444] hover:text-[#FF4081] transition-colors ${
              isActive("/") ? "text-[#FF4081] font-semibold underline" : ""
            }`}
          >
            Accueil
          </button>
          <button
            onClick={() => {
              scrollToSection(navigate, "/services", "services");
              toggleMenu();
            }}
            className={`w-full text-left font-poppins font-medium text-[15px] text-[#444] hover:text-[#FF4081] transition-colors ${
              isActive("/services")
                ? "text-[#FF4081] font-semibold underline"
                : ""
            }`}
          >
            Services
          </button>
          <button
            onClick={() => {
              scrollToSection(navigate, "/plan-abonnements", "");
              toggleMenu();
            }}
            className={`w-full text-left font-poppins font-medium text-[15px] text-[#444] hover:text-[#FF4081] transition-colors ${
              isActive("/plan-abonnements")
                ? "text-[#FF4081] font-semibold underline"
                : ""
            }`}
          >
            Plan d'abonnements
          </button>
          <button
            onClick={() => {
              scrollToSection(navigate, "/", "apropos");
              toggleMenu();
            }}
            className={`w-full text-left font-poppins font-medium text-[15px] text-[#444] hover:text-[#FF4081] transition-colors ${
              isActive("/about") ? "text-[#FF4081] font-semibold underline" : ""
            }`}
          >
            A Propos
          </button>
          <button
            onClick={() => {
              scrollToSection(navigate, "/", "contact");
              toggleMenu();
            }}
            className={`w-full text-left font-poppins font-medium text-[15px] text-[#444] hover:text-[#FF4081] transition-colors ${
              isActive("/contact")
                ? "text-[#FF4081] font-semibold underline"
                : ""
            }`}
          >
            Contact
          </button>

          <Link
            to="/cart"
            onClick={toggleMenu}
            className="w-full bg-black text-white px-4 py-3 flex items-center justify-center gap-2 font-nunito font-semibold text-[14px] rounded hover:bg-gray-800 transition-colors"
          >
            <CiCalendar size={18} />
            Réserver
          </Link>

          {user ? (
            <div className="relative" ref={mobileDropdownRef}>
              <button
                onClick={toggleMobileDropdown}
                className="w-full flex justify-between items-center px-2 py-3 font-poppins font-medium text-[15px] text-[#444] hover:text-[#FF4081] transition-colors border-t border-gray-100 mt-2"
              >
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                    <FiUser size={16} />
                  </div>
                  <span>{`${user.prenom} ${user.nom}` || "Profil"}</span>
                </div>
                <FiChevronDown
                  size={16}
                  className={`transition-transform duration-200 ${isMobileDropdownOpen ? "rotate-180" : ""}`}
                />
              </button>

              {/* Dropdown Mobile */}
              {isMobileDropdownOpen && (
                <div className="pl-4 mt-2 border-l-2 border-gray-100">
                  <UserDropdownContent isMobile={true} />
                </div>
              )}
            </div>
          ) : (
            <Link
              to="/login"
              onClick={toggleMenu}
              className="w-full flex items-center gap-2 px-2 py-3 font-poppins font-medium text-[15px] text-[#444] hover:text-[#FF4081] transition-colors border-t border-gray-100 mt-2"
            >
              <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                <FiUser size={16} />
              </div>
              <span>Connexion</span>
            </Link>
          )}
        </div>
      </div>
    </>
  );
};

export default NavbarSection;
